import {ClassComponent, Vnode} from 'mithril';
import app from 'flarum/forum/app';
import Button from 'flarum/common/components/Button';
import Dropdown from 'flarum/common/components/Dropdown';
import LinkButton from 'flarum/common/components/LinkButton';
import extractText from 'flarum/common/utils/extractText';
import ItemList from 'flarum/common/utils/ItemList';
import submissionCount from '../utils/submissionCount';
import DiscreetInput from './DiscreetInput';
import Form from '../models/Form';
import {formulaireSubmissionIndex} from '../routes';

const translationPrefix = 'kilowhat-formulaire.forum.form-edit-header.';

const TABS = [
    {
        key: 'template',
        icon: 'fas fa-poll-h',
    },
    {
        key: 'placement',
        icon: 'fas fa-puzzle-piece',
    },
    {
        key: 'access',
        icon: 'fas fa-user-shield',
    },
    {
        key: 'notifications',
        icon: 'fas fa-envelope',
    },
];

interface FormEditHeaderAttrs {
    form: Form
    editable?: boolean
    validationErrors?: any
    updateAttribute?: (attribute: string, value: string) => void
    tab?: string
    changeTab: (tab: string) => void
    isSubmissionPage?: boolean
}

export default class FormEditHeader implements ClassComponent<FormEditHeaderAttrs> {
    restoring: boolean = false
    deleting: boolean = false

    view(vnode: Vnode<FormEditHeaderAttrs, this>) {
        const {form, editable, validationErrors = {}} = vnode.attrs;

        const controls = this.controls(form, vnode).toArray();

        let backButtonHref;

        switch (form.type()) {
            case 'standalone':
                backButtonHref = app.route('formulaireFormIndex');
                break;
            case 'discussion':
                backButtonHref = app.route('formulaireFormIndexDiscussion');
                break;
            case 'user':
                backButtonHref = app.route('formulaireFormIndexUser');
                break;
        }

        return m('.FormulaireHeader', [
            m(Dropdown, {
                className: 'FormulaireHeader-controls App-primaryControl',
                menuClassName: 'Dropdown-menu--right',
                buttonClassName: 'Button Button--icon Button--flat',
                label: app.translator.trans(translationPrefix + 'controls'),
                icon: 'fas fa-ellipsis-v',
            }, controls),
            m('.FormulaireHeader-back', [
                LinkButton.component({
                    icon: 'fas fa-arrow-left',
                    href: backButtonHref,
                    className: 'Button Button--flat',
                }, app.translator.trans(translationPrefix + 'category.' + form.type())),
            ]),
            m('h1', m(DiscreetInput, {
                title: app.translator.trans(translationPrefix + 'fields.private-title'),
                editable,
                value: form.private_title(),
                // Usually a title is set at creation before seeing this, so the empty field will rarely be seen
                // Unless the text is completely cleared
                placeholder: app.translator.trans(translationPrefix + 'fields.private-title'),
                onchange: (value: string) => {
                    vnode.attrs.updateAttribute!('private_title', value);
                },
                validationErrors: validationErrors.private_title,
            })),
            (form.type() !== 'discussion' || form.isAutoLink()) ? m('.FormulaireHeader-url', [
                m('a', {
                    href: form.type() === 'user' ? app.route('formulaireProfile', {
                        username: app.session.user!.username(),
                        form: form.seoId(),
                    }) : app.route('formulaireFormShow', {
                        id: form.seoId(),
                    }),
                    target: '_blank',
                }, app.forum.attribute('baseUrl') + (form.type() === 'user' ? app.route('formulaireProfile', {
                    username: app.session.user!.username(),
                    form: '',
                }) : app.route('formulaireFormShow', {
                    id: '',
                }))),
                ' ',
                m(DiscreetInput, {
                    title: app.translator.trans(translationPrefix + 'fields.slug'),
                    editable,
                    value: form.slug(),
                    placeholder: form.id(),
                    onchange: (value: string) => {
                        vnode.attrs.updateAttribute!('slug', value);
                    },
                    validationErrors: validationErrors.slug,
                }),
            ]) : null,
            m('.FormulaireTabs', [
                TABS.filter(tab => {
                    // Only show the notifications tab for form types that support some kind of notification
                    if (tab.key === 'notifications') {
                        return form.type() === 'standalone' || form.isAutoLink();
                    }

                    return true;
                }).map(tab => m(Button, {
                    className: vnode.attrs.tab === tab.key ? 'active' : '',
                    icon: tab.icon,
                    onclick: () => {
                        vnode.attrs.changeTab(tab.key);
                    },
                }, [
                    app.translator.trans(translationPrefix + 'tabs.' + tab.key),
                    tab.key === 'access' ? this.accessStatus(form) : null,
                ])),
                m(LinkButton, {
                    className: vnode.attrs.isSubmissionPage ? 'active' : '',
                    icon: 'fas fa-list',
                    href: formulaireSubmissionIndex(form),
                }, [
                    app.translator.trans(translationPrefix + 'tabs.submissions'),
                    ' ',
                    m('span.badge', submissionCount(form)),
                ]),
            ]),
        ]);
    }

    controls(form: Form, vnode: Vnode<FormEditHeaderAttrs, this>) {
        const items = new ItemList();

        items.add('export', Button.component({
            icon: 'fas fa-file-export',
            onclick: () => {
                vnode.attrs.changeTab('export');
            },
        }, app.translator.trans(translationPrefix + 'template-export')));

        const expertMode = localStorage.getItem('formulaireExpertMode') === 'true';

        items.add('expert', Button.component({
            icon: 'fas fa-toolbox',
            onclick() {
                if (expertMode) {
                    localStorage.removeItem('formulaireExpertMode');
                } else {
                    localStorage.setItem('formulaireExpertMode', 'true');
                }
            },
        }, app.translator.trans(translationPrefix + (expertMode ? 'disable' : 'enable') + '-expert-mode')));

        if (form.isHidden() && form.attribute('canRestore')) {
            items.add('hide', Button.component({
                icon: 'fas fa-reply',
                loading: this.restoring,
                onclick: () => {
                    this.restoring = true;

                    form.save({
                        isHidden: false,
                    }).then(() => {
                        this.restoring = false;
                        m.redraw();
                    }).catch(err => {
                        this.restoring = false;
                        m.redraw();
                        throw err;
                    });
                },
            }, app.translator.trans(translationPrefix + 'restore')));
        }

        if (!form.isHidden() && form.attribute('canHide')) {
            items.add('hide', Button.component({
                icon: 'far fa-trash-alt',
                loading: this.deleting,
                onclick: () => {
                    this.deleting = true;

                    form.save({
                        isHidden: true,
                    }).then(() => {
                        this.deleting = false;
                        m.redraw();
                    }).catch(err => {
                        this.deleting = false;
                        m.redraw();
                        throw err;
                    });
                },
            }, app.translator.trans(translationPrefix + 'hide')));
        }

        if (form.isHidden() && form.attribute('canDelete')) {
            items.add('delete', Button.component({
                icon: 'fas fa-times',
                loading: this.deleting,
                onclick: () => {
                    if (confirm(extractText(app.translator.trans(translationPrefix + 'delete-confirmation', {
                        title: form.title(),
                    })))) {
                        this.deleting = true;

                        form.delete().then(() => {
                            this.deleting = false;
                            m.redraw();

                            m.route.set(app.route('formulaireFormIndex'));
                        }).catch(err => {
                            this.deleting = false;
                            m.redraw();
                            throw err;
                        });
                    }
                },
            }, app.translator.trans(translationPrefix + 'delete')));
        }

        return items;
    }

    accessStatus(form: Form) {
        const create = form.accept_submissions();
        const edit = form.allow_modification();

        let key = 'closed';

        if (form.type() !== 'standalone') {
            key = create ? 'enabled' : 'disabled';
        } else if (create && edit) {
            key = 'both';
        } else if (create) {
            key = 'create';
        } else if (edit) {
            key = 'edit';
        }

        return [
            ' ',
            m('span.badge', app.translator.trans(translationPrefix + 'access-status.' + key)),
        ];
    }
}
